import { Box, Spinner, Table } from '@factorialco/deprecated-design-system'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import { PayrollIssuesTableIssueFragment } from 'generated/resources/graphql'
import isEmpty from 'lodash/isEmpty'
import orderBy from 'lodash/orderBy'

import { observer } from 'mobx-react'

import useEmployeeMultiFilters from 'modules/core/components/EmployeeMultiFilters/hooks/useEmployeeMultiFilters'
import employeeStatuses from 'modules/core/components/EmployeeMultiFilters/hooks/utils/employeeStatuses'
import FILTER_OPTIONS from 'modules/core/components/Filters/filterOptions'
import createComputed from 'modules/core/hooks/createComputed'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import FiltersForm from 'modules/core/lib/forms/Filters'
import { matchText, normalizeText } from 'modules/core/lib/stringUtils'

import SearchBar from 'modules/payroll_bundle/components/common/components/_SearchBar'

import Header from './Header'
import Item from './Item'
import filterValues from './filterPayrollIssuesTable'

type Issue = PayrollIssuesTableIssueFragment
interface Props {
  payrollIssues: Issue[]
  loading: boolean
}

const orderIssues = createComputed((items: Issue[], filtersForm: FiltersForm): Issue[] => {
  const sortAttribute = filtersForm.get('sortAttribute').value
  const sortDirection = filtersForm.get('sortDirection').value

  return orderBy(
    items,
    [
      (item: Issue) => {
        if (sortAttribute === 'full_name') {
          return normalizeText(item.employee?.fullName ?? '')
        }
      },
    ],
    [sortDirection]
  )
})

const useSearchFilter = createComputed((search: string, issues: Issue[]) => {
  if (isEmpty(search)) return issues

  return issues.filter((item) => {
    return (
      matchText(search, item.title) || (item.employee && matchText(search, item.employee.fullName))
    )
  })
})

const FILTER_KEY = LOCAL_STORAGE_KEYS.payrollIssues

const PayrollIssuesTable = ({ payrollIssues, loading }: Props) => {
  const DEFAULT_FILTER_VALUES = [
    {
      field: 'employee_status',
      value: employeeStatuses.active,
    },
    {
      field: 'employee_status',
      value: employeeStatuses.terminated,
    },
  ]

  const EMPLOYEE_FILTER_OPTIONS = {
    skipDefaultFilters: DEFAULT_FILTER_VALUES,
  }
  const filtersForm = useLazyRef(
    () =>
      new FiltersForm({
        sortAttribute: 'full_name',
        sortDirection: 'asc',
        filterBy: FILTER_OPTIONS.activity.all,
      })
  )
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()

  const {
    filteredModels: filteredPayrollIssues,
    logicOperator,
    filters,
    onApplyFilters,
    onCancel,
    onRemoveFilter,
    onAddFilter,
  } = useEmployeeMultiFilters<Issue>(filterValues, FILTER_KEY, EMPLOYEE_FILTER_OPTIONS)

  const filteredIssues = useSearchFilter(debouncedSearch, filteredPayrollIssues).get()
  const issuesToShow = orderIssues(filteredIssues, filtersForm.current).get()

  useUnsafeEffect(() => {
    onApplyFilters(payrollIssues)
  }, [payrollIssues]) // eslint-disable-line react-hooks/exhaustive-deps

  const Options = () => (
    <Box
      width="full"
      alignItems="center"
      flexDirection="row"
      background="grey200"
      justifyContent="spaceBetween"
    >
      <SearchBar
        disabled={loading}
        search={search}
        onSearch={onSearch}
        onCancel={onCancel}
        filters={filters}
        onAddFilter={onAddFilter}
        logicOperator={logicOperator}
        onRemoveFilter={onRemoveFilter}
        onApplyFilters={() => onApplyFilters(payrollIssues)}
      />
    </Box>
  )

  return (
    <Table autoLayout fullWidth options={Options}>
      {loading ? (
        <Table.Body>
          <Table.Row>
            <Table.Cell>
              <Box width="full" alignItems="center" justifyContent="center">
                <Spinner />
              </Box>
            </Table.Cell>
          </Table.Row>
        </Table.Body>
      ) : (
        <>
          <Header filtersForm={filtersForm.current} />
          <Table.Body>
            {issuesToShow.map((issue) => (
              <Item key={issue.id} issue={issue} />
            ))}
          </Table.Body>
        </>
      )}
    </Table>
  )
}

export default observer(PayrollIssuesTable)
